/**
 * EN-1516: client-side "AGM UIDs" filter for already-loaded lists (the
 * Download Manager's queued commands). Pure functions so they can be unit
 * tested; e2e/agm-uid-filter.unit.spec.ts covers them.
 *
 * The filter only narrows rows already in memory. GET /commands/queued is
 * capped at 500 rows (newest first), so a UID whose rows fall outside that cap
 * will not match here.
 */

/**
 * Splits the filter text into UIDs: commas, spaces, tabs and newlines all
 * separate. Each UID is trimmed and lower-cased; empty pieces (trailing or
 * repeated separators) and duplicates are dropped. Order is kept.
 */
export function parseAgmUidFilter(text: string | null | undefined): string[] {
  if (!text) return [];
  const uids: string[] = [];
  for (const piece of text.split(/[\s,]+/)) {
    const uid = piece.trim().toLowerCase();
    if (uid !== '' && !uids.includes(uid)) uids.push(uid);
  }
  return uids;
}

/**
 * The items whose UID is one of the UIDs in `text`, compared exactly with the
 * UID as displayed (`String(uid)`, case-insensitive). An empty filter returns
 * every item. Returns a new array in the input order; the input is not
 * mutated.
 */
export function filterByAgmUids<T>(
  items: readonly T[],
  text: string | null | undefined,
  uidOf: (item: T) => number | string | null | undefined
): T[] {
  const uids = parseAgmUidFilter(text);
  if (uids.length === 0) return [...items];
  const wanted = new Set(uids);
  return items.filter((item) => {
    const uid = uidOf(item);
    if (uid === null || uid === undefined) return false;
    return wanted.has(String(uid).trim().toLowerCase());
  });
}
